// ============================================
// LiveChatEngine — Spec-Driven Nurse Conversation
//
// Rejection-first: no response reaches the patient
// without surviving NRS.03 → NRS.04 filter chain.
//
// No hardcoded messages. No protocol before triage.
// Every nurse response earned its way through the membrane.
//
// Phase flow: PRE-ANALYSIS → MID-ANALYSIS → POST-TRIAGE
// Spec: nurse-bone-complete-v1.md (LOCKED 2026-03-14)
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  ChatDataSource,
  BarMarker,
  BarSymptom,
  BarProtocol,
} from '../../types/analystBar';
import type { BiomarkerResult } from '../../types';
import type { CapturedSignal } from '../../types/fashionPlate';
import type { AnalystPhase2Output, ModifierSet } from '../../types/patientJourney';
import type { GeminiMessage } from '../../services/ai/providers/gemini';
import type { NurseOrchestrator } from '../../services/nurse/NurseOrchestrator';
import { AnalystBar } from './analyst-bar/AnalystBar';
import { useApp } from '../../context/AppContext';
import { SaveProgressBanner } from '../shared/SaveProgressBanner';
import { SEED_SUPPLEMENTS } from '../../data/formulary/seedSupplements';
import { SEED_RX } from '../../data/formulary/seedRx';
import { SEED_LONGEVITY } from '../../data/formulary/seedLongevity';
import { parseMonthlyPrice } from '../../services/patientJourney/twinEngine';
import { mapBiomarkerToBarMarker } from '../../services/agents/labAnalystStream';
import { T, F } from '../quality/tokens';
// No GoalsModal — the box IS the goal (feedback-goals-are-boxes.md)
import { MedsModal } from '../modals/MedsModal';
import { AllergiesModal } from '../modals/AllergiesModal';
import { PregnancyModal } from '../modals/PregnancyModal';
import { EmailSaveModal } from '../modals/EmailSaveModal';

const ALL_PRODUCTS_FOR_PRICE = [...SEED_SUPPLEMENTS, ...SEED_RX, ...SEED_LONGEVITY];

const R = 4;
const R_LG = T.radius.lg;
const CONTRAST_WHITE = '#FFFFFF';

// ── Modal marker patterns (NRS.03 output) ──
const MODAL_MARKERS = [
  '[SHOW_GOALS_MODAL]',
  '[SHOW_MEDS_MODAL]',
  '[SHOW_ALLERGIES_MODAL]',
  '[SHOW_PREGNANCY_MODAL]',
  '[SHOW_REPORT]',
  '[BUILD_PROTOCOL]',
  '[SHOW_EMAIL_MODAL]',
] as const;

// No 'goals' — the box IS the goal (goals-are-boxes). Nurse probes goals conversationally.
type ActiveModal = 'meds' | 'allergies' | 'pregnancy' | null;

// ── System prompt — NO doctor name, NO patient name ──
// The membrane starts here. Every instruction constrains what the LLM
// can say. NRS.04 catches what leaks through. Double rejection.
function buildLiveSystemPrompt(
  phase: 'pre-analysis' | 'mid-analysis' | 'post-triage',
  patientSex?: 'male' | 'female',
  patientAge?: number,
  biomarkers?: BiomarkerResult[],
  protocol?: AnalystPhase2Output | null,
  signals?: CapturedSignal[],
): string {
  const sexLabel = patientSex === 'male' ? 'Male' : patientSex === 'female' ? 'Female' : 'Unknown';
  const ageLabel = patientAge ? `${patientAge} years old` : 'age unknown';

  let prompt = `You are a HemoLogix health navigator following a functional medicine methodology backed by 40 years of clinical practice.

ROLE: You are the Health Navigator — warm, knowledgeable, never diagnostic. You identify gaps vs optimal ranges, NOT diagnoses. You never say "you have" or "you suffer from." You say "your levels suggest" or "this pattern is consistent with."

PATIENT: ${sexLabel}, ${ageLabel}.

STRICT RULES:
- Never fabricate data — only reference biomarkers listed below
- Never diagnose disease — identify gaps vs optimal ranges
- Never introduce yourself by a personal name
- NO HOLLOW PROMISES: Never promise to "text results" or "send a link to your phone." We don't have that feature. If they want to save progress, use [SHOW_EMAIL_MODAL].
- Explain that results are saved to their secure account, accessible via email link.
- CONCIERGE SPEED: If the patient is in a hurry, has a poor connection, or asks for results directly, you MUST consolidate. Ask remaining intake questions (meds, allergies, goals, pregnancy) in a single turn.
- FAST-TRACK: If they persist, deliver the top 2 findings IMMEDIATELY and trigger [SHOW_REPORT]. Safety comes first, but don't hold their data hostage.
- When patient describes symptoms (tired, sleepy, foggy, etc.), ask probing follow-up questions.
- Keep responses concise (2-3 sentences). Warm but efficient.
- No emojis. Professional but approachable tone.
`;

  if (phase === 'pre-analysis') {
    prompt += `
CURRENT PHASE: Pre-Analysis. Lab results are being processed in the background.
- Greet the patient warmly and ask about their health concerns
- Ask about symptoms, goals, what brought them here
- Do NOT reference specific lab values — they are not ready yet
- You may say "your labs are being analyzed" if asked
`;
  }

  if (phase === 'mid-analysis' && biomarkers) {
    const suboptimal = biomarkers
      .filter(b => b.hemologixStatus !== 'optimal')
      .map(b => `${b.biomarker}: ${b.value} ${b.unit} (optimal: ${b.functionalRange.low}-${b.functionalRange.high}, status: ${b.hemologixStatus})`)
      .join('\n');

    prompt += `
CURRENT PHASE: Mid-Analysis. Lab results are available.

BIOMARKERS (suboptimal/flagged):
${suboptimal || 'All within optimal ranges'}

- Reference specific biomarker values naturally (e.g., "your Ferritin at 22")
- Guide the conversation toward collecting health goals, medications, and allergies
- The patient's program track (box) is already set — do NOT ask about health goals via a form
- Use these markers when the conversation naturally reaches the right moment:
  [SHOW_MEDS_MODAL] — when ready to ask about current medications
  [SHOW_ALLERGIES_MODAL] — when ready to ask about allergies
  [SHOW_PREGNANCY_MODAL] — when appropriate for female patients
- Only use ONE marker per response
- After all intake data is collected, use [BUILD_PROTOCOL] to trigger the protocol
`;
  }

  if (phase === 'post-triage' && protocol) {
    const protocolSummary = protocol.protocol
      .map(p => `- ${p.productName}: ${p.dosage} (for ${p.triggerBiomarker})`)
      .join('\n');

    prompt += `
CURRENT PHASE: Post-Triage. Protocol is built and displayed to the patient.

RECOMMENDED PROTOCOL (Gate ${protocol.gateLevel ?? 1}):
${protocolSummary}

- Answer questions about the protocol, biomarkers, or next steps
- Never contradict the protocol — it was built by the formulary engine
- Explain how specific supplements address specific biomarker gaps
- If asked about cost, refer to the protocol card already displayed
`;

    // Include biomarker context in post-triage too
    if (biomarkers) {
      const suboptimal = biomarkers
        .filter(b => b.hemologixStatus !== 'optimal')
        .map(b => `${b.biomarker}: ${b.value} ${b.unit} (status: ${b.hemologixStatus})`)
        .join('\n');
      prompt += `
BIOMARKERS:
${suboptimal}
`;
    }
  }

  if (signals && signals.length > 0) {
    const signalSummary = signals.map(s => `- ${s.term} (${s.category})`).join('\n');
    prompt += `
CAPTURED SIGNALS FROM CONVERSATION:
${signalSummary}
`;
  }

  return prompt;
}

// ── Strip modal markers from display text ──
function stripMarkers(text: string): string {
  let cleaned = text;
  for (const marker of MODAL_MARKERS) {
    while (cleaned.includes(marker)) {
      cleaned = cleaned.replace(marker, '');
    }
  }
  return cleaned.trim();
}

// ── Extract first marker from text ──
function findMarker(text: string): string | null {
  for (const marker of MODAL_MARKERS) {
    if (text.includes(marker)) return marker;
  }
  return null;
}

// ── Shared visual utilities ──

function signalsToBarSymptoms(signals: CapturedSignal[]): BarSymptom[] {
  return signals
    .filter(s => s.category === 'symptom')
    .map(s => ({ term: s.term.toUpperCase(), sourceText: s.sourceText, linkedBiomarker: s.linkedBiomarker }));
}

function HighlightedText({ text, activeSymptoms }: { text: string; activeSymptoms: BarSymptom[] }) {
  if (!activeSymptoms || !activeSymptoms.length) return <>{text}</>;
  const matches: { start: number; end: number; word: string }[] = [];
  const textLower = text.toLowerCase();
  for (const symptom of activeSymptoms) {
    if (!symptom.sourceText) continue;
    const idx = textLower.indexOf(symptom.sourceText.toLowerCase());
    if (idx >= 0) matches.push({ start: idx, end: idx + symptom.sourceText.length, word: text.slice(idx, idx + symptom.sourceText.length) });
  }
  if (!matches.length) return <>{text}</>;
  matches.sort((a, b) => a.start - b.start);
  const parts: React.ReactNode[] = [];
  let c = 0;
  for (const m of matches) {
    if (m.start < c) continue;
    if (m.start > c) parts.push(<span key={c}>{text.slice(c, m.start)}</span>);
    parts.push(
      <span key={m.start} style={{ background: 'rgba(255,255,255,0.28)', borderRadius: 3, padding: '1px 5px', margin: '0 -2px', fontWeight: 700, borderBottom: '2px solid #FFFFFF' }}>{m.word}</span>,
    );
    c = m.end;
  }
  if (c < text.length) parts.push(<span key={c}>{text.slice(c)}</span>);
  return <>{parts}</>;
}

// ── Icons ──
const HeartPulseIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" /><path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" /></svg>;
const ShieldIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textGhost} strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
const FileCheckIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><path d="m9 15 2 2 4-4" /></svg>;
const CheckIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={CONTRAST_WHITE} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>;
const SendIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={CONTRAST_WHITE} strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>;

function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '4px 0' }}>
      {[0, 1, 2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: T.accent, animation: `td 1.4s ease-in-out ${i * 0.16}s infinite` }} />)}
    </div>
  );
}

// ── Protocol Card (only shown after [BUILD_PROTOCOL]) ──
function ProtocolCard({ dataSource, dailyPrice }: { dataSource: ChatDataSource; dailyPrice: string }) {
  const [started, setStarted] = useState(false);
  const bd = dataSource.brandDisplay;

  const trustSignals = [
    'Functional medicine methodology',
    `Retest at ${bd.retestWeeks} weeks`,
    'Cancel anytime',
  ];

  const objectionKillers = [
    'No prescription needed',
    `Ships in ${bd.shippingDays} days`,
    ...(bd.hsaFsaEligible ? ['HSA/FSA eligible'] : []),
  ];

  return (
    <div style={{
      margin: '8px 0 4px', padding: '16px', background: T.bgCard, border: `1px solid ${T.border}`,
      borderRadius: R_LG, boxShadow: T.shadowSm, animation: 'msgIn 0.3s ease',
    }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
        {trustSignals.map(t => (
          <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: T.fontSize.xs, color: T.textMuted, fontFamily: F.body }}>
            <div style={{ width: 4, height: 4, borderRadius: '50%', background: T.success, flexShrink: 0 }} />
            {t}
          </div>
        ))}
      </div>

      <button
        onClick={() => setStarted(true)}
        style={{
          width: '100%', padding: '14px 20px', fontSize: T.fontSize.base, fontWeight: 700,
          color: CONTRAST_WHITE, background: started ? T.success : T.accent,
          border: 'none', borderRadius: R, cursor: 'pointer',
          fontFamily: F.body,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'all 0.3s ease',
          boxShadow: started ? 'none' : T.shadowAccent,
        }}
      >
        {started ? <><CheckIcon /> Protocol Started</> : `Start My Protocol \u2014 $${dailyPrice}/day`}
      </button>

      {!started && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10 }}>
          {objectionKillers.map(t => (
            <span key={t} style={{ fontSize: T.fontSize.xs, color: T.textGhost, fontFamily: F.body }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN — LiveChatEngine
// ============================================

interface ChatMessage {
  id: number;
  role: 'nurse' | 'user' | 'system';
  text: string;
}

interface LiveChatEngineProps {
  dataSource: ChatDataSource;
  orchestrator: NurseOrchestrator;
  onProtocolStart?: (protocol: AnalystPhase2Output) => void;
}

export function LiveChatEngine({ dataSource, orchestrator, onProtocolStart }: LiveChatEngineProps) {
  const { state: appState } = useApp();

  // Biomarkers from Captain extraction (may arrive after mount)
  const biomarkers = appState.labReport?.biomarkers ?? null;

  // ── State ──
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [tickerMarkers, setTickerMarkers] = useState<BarMarker[]>([]);
  const [symptoms, setSymptoms] = useState<BarSymptom[]>([]);
  const [captainMsg, setCaptainMsg] = useState<string | null>(null);
  const [protocol, setProtocol] = useState<BarProtocol | null>(null);
  const [showCard, setShowCard] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [builtProtocol, setBuiltProtocol] = useState<AnalystPhase2Output | null>(null);
  const [phase, setPhase] = useState<'pre-analysis' | 'mid-analysis' | 'post-triage'>('pre-analysis');

  // ── Refs ──
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const signalsRef = useRef<CapturedSignal[]>([]);
  const turnRef = useRef(0);
  const chatHistoryRef = useRef<GeminiMessage[]>([]);
  const nextIdRef = useRef(1);
  const hasBooted = useRef(false);

  const nextId = useCallback(() => nextIdRef.current++, []);
  const addMsg = useCallback((msg: ChatMessage) => setMessages(prev => [...prev, msg]), []);

  // ── Auto-scroll ──
  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 80);
    }
  }, [messages, isTyping, showCard, activeModal]);

  // ── Detect when biomarkers arrive (Captain finished) ──
  useEffect(() => {
    if (biomarkers && biomarkers.length > 0 && phase === 'pre-analysis') {
      setPhase('mid-analysis');
      setTickerMarkers(biomarkers.map(mapBiomarkerToBarMarker));
      setCaptainMsg('Lab results analyzed');
      setTimeout(() => setCaptainMsg(null), 3000);
    }
  }, [biomarkers, phase]);

  // ── Initial greeting via orchestrator (NRS.03 → NRS.04) ──
  // No hardcoded messages. The nurse earns her opening line through the membrane.
  useEffect(() => {
    if (hasBooted.current) return;
    hasBooted.current = true;

    // If biomarkers already available on mount, start in mid-analysis
    const startPhase = (biomarkers && biomarkers.length > 0) ? 'mid-analysis' : 'pre-analysis';
    if (startPhase === 'mid-analysis') {
      setPhase('mid-analysis');
      setTickerMarkers(biomarkers!.map(mapBiomarkerToBarMarker));
    }

    setIsTyping(true);
    // Resume from saved session if available (#5 session resume)
    if (appState.chatHistory && appState.chatHistory.length > 0) {
      const restored: ChatMessage[] = appState.chatHistory.map((m, i) => ({
        id: i + 1,
        role: m.role as 'nurse' | 'user' | 'system',
        text: m.content,
      }));
      setMessages(restored);
      nextIdRef.current = restored.length + 1;
      if (startPhase === 'mid-analysis') {
        setTickerMarkers(biomarkers!.map(mapBiomarkerToBarMarker));
      }
      setIsTyping(false);
      setTimeout(() => inputRef.current?.focus(), 300);
      return;
    }

    const patientSex = appState.triageData?.sex === 'male' ? 'male' as const : 'female' as const;
    const patientAge = appState.triageData?.age;
    const greetingPrompt = buildLiveSystemPrompt(startPhase, patientSex, patientAge, biomarkers ?? undefined);

    orchestrator.nurseRespond([], greetingPrompt, '[SESSION_START]')
      .then(response => {
        setIsTyping(false);
        const displayText = stripMarkers(response);
        if (displayText) {
          addMsg({ id: nextId(), role: 'nurse', text: displayText });
          chatHistoryRef.current.push({ role: 'model', parts: [{ text: displayText }] });
        }
        setTimeout(() => inputRef.current?.focus(), 100);
      })
      .catch(err => {
        setIsTyping(false);
        console.error('[LiveChatEngine] Initial greeting failed:', err);
        // Fail closed: no hardcoded fallback. Input is available — patient can initiate.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handle modal markers in nurse responses ──
  const handleMarkerInResponse = useCallback((marker: string) => {
    const modalCheck = orchestrator.shouldShowModal(marker);

    switch (marker) {
      case '[SHOW_GOALS_MODAL]':
        // No-op: box IS the goal (goals-are-boxes). Nurse probes conversationally.
        break;
      case '[SHOW_MEDS_MODAL]':
        if (modalCheck.show) setActiveModal('meds');
        break;
      case '[SHOW_ALLERGIES_MODAL]':
        if (modalCheck.show) setActiveModal('allergies');
        break;
      case '[SHOW_PREGNANCY_MODAL]':
        if (modalCheck.show) setActiveModal('pregnancy');
        break;
      case '[SHOW_EMAIL_MODAL]':
        setShowEmailModal(true);
        break;
      case '[BUILD_PROTOCOL]': {
        const modifiers = orchestrator.getModifiers();
        const proto = dataSource.getProtocol(biomarkers ?? [], modifiers);
        const barProducts = proto.protocol.map(p => {
          const seed = ALL_PRODUCTS_FOR_PRICE.find(s => s.id === p.productId);
          const mp = parseMonthlyPrice(seed?.pricing?.monthlyPrice);
          return { name: p.productName, monthlyPrice: mp };
        });
        const totalMonthly = barProducts.reduce((sum, p) => sum + p.monthlyPrice, 0);

        setProtocol({
          gateLabel: `GATE ${proto.gateLevel ?? 1} \u00B7 PROTOCOL`,
          products: barProducts,
          dailyPrice: totalMonthly / 30,
        });
        setShowCard(true);
        setBuiltProtocol(proto);
        setPhase('post-triage');
        setCaptainMsg(`Gate ${proto.gateLevel ?? 1} \u00B7 protocol matched`);
        setTimeout(() => setCaptainMsg(null), 3000);
        if (onProtocolStart) onProtocolStart(proto);
        break;
      }
    }
  }, [orchestrator, dataSource, biomarkers, onProtocolStart]);

  // ── Modal submission handlers ──

  // No handleGoalsSubmit — box IS the goal. Goals modal removed.

  const handleMedsSubmit = useCallback((meds: string[]) => {
    orchestrator.processModalSubmission('medications', { type: 'medications', medications: meds });
    addMsg({ id: nextId(), role: 'user', text: meds.length > 0 ? `Current meds: ${meds.join(', ')}` : 'No current medications' });
    setActiveModal(null);
  }, [orchestrator, addMsg, nextId]);

  const handleAllergiesSubmit = useCallback((allergies: string[]) => {
    orchestrator.processModalSubmission('allergies', { type: 'allergies', allergies });
    addMsg({ id: nextId(), role: 'user', text: allergies.length > 0 ? `Allergies: ${allergies.join(', ')}` : 'No known allergies' });
    setActiveModal(null);
  }, [orchestrator, addMsg, nextId]);

  const handlePregnancySubmit = useCallback((status: ModifierSet['reproductiveStatus']) => {
    const labels: Record<ModifierSet['reproductiveStatus'], string> = {
      'pregnant': "Yes, I\u2019m pregnant",
      'trying_to_conceive': 'Trying to conceive',
      'breastfeeding': 'Breastfeeding',
      'postmenopausal': 'Postmenopausal',
      'not_applicable': 'None of these apply',
    };
    orchestrator.processModalSubmission('pregnancy', {
      type: 'pregnancy',
      isPregnant: status === 'pregnant',
      isTryingToConceive: status === 'trying_to_conceive',
      isBreastfeeding: status === 'breastfeeding',
    });
    addMsg({ id: nextId(), role: 'user', text: labels[status] });
    setActiveModal(null);
  }, [orchestrator, addMsg, nextId]);

  // ── Send user message through the full pipeline ──
  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || isSending) return;

    setInputText('');
    setIsSending(true);

    const turn = ++turnRef.current;
    const msgId = nextId();

    // Add user message
    addMsg({ id: msgId, role: 'user', text });
    chatHistoryRef.current.push({ role: 'user', parts: [{ text }] });

    // NRS.06: Scan for disclosures
    orchestrator.scanForDisclosures(text, turn);

    // Extract symptoms (non-blocking)
    try {
      const newSignals = await dataSource.extractSymptoms(text, turn, signalsRef.current);
      if (newSignals.length > signalsRef.current.length) {
        signalsRef.current = newSignals;
        setSymptoms(signalsToBarSymptoms(newSignals));
      }
    } catch { /* extraction failure is non-blocking */ }

    // Check for late reveals (safety-critical: medication, allergy, condition after protocol)
    const reveal = dataSource.detectLateReveal(text, turn);
    if (reveal && reveal.needsRerun && builtProtocol) {
      const modifiers = orchestrator.getModifiers();
      const newProto = dataSource.getProtocol(biomarkers ?? [], modifiers);
      const newBarProducts = newProto.protocol.map(p => {
        const seed = ALL_PRODUCTS_FOR_PRICE.find(s => s.id === p.productId);
        const mp = parseMonthlyPrice(seed?.pricing?.monthlyPrice);
        return { name: p.productName, monthlyPrice: mp };
      });
      const newTotal = newBarProducts.reduce((sum, p) => sum + p.monthlyPrice, 0);

      setCaptainMsg('Protocol updated \u00B7 safety modifier detected');
      setProtocol({
        gateLabel: `GATE ${newProto.gateLevel ?? 1} \u00B7 UPDATED PROTOCOL`,
        products: newBarProducts,
        dailyPrice: newTotal / 30,
      });
      setBuiltProtocol(newProto);
      addMsg({ id: nextId(), role: 'system', text: `Safety update: ${reveal.safetyChanges.join(', ')} detected` });
      setTimeout(() => setCaptainMsg(null), 3000);
    }

    // Build phase-aware system prompt (NO doctor name — membrane rule)
    const currentPhase = orchestrator.getState().phase;
    const patientSex = appState.triageData?.sex === 'male' ? 'male' as const : 'female' as const;
    const patientAge = appState.triageData?.age;
    const reinforcement = dataSource.getNurseReinforcement(signalsRef.current, turn);
    const systemPrompt = buildLiveSystemPrompt(
      currentPhase,
      patientSex,
      patientAge,
      biomarkers ?? undefined,
      builtProtocol,
      signalsRef.current,
    ) + (reinforcement ? `\nREINFORCEMENT:\n${reinforcement}` : '');

    // Call orchestrator → NRS.03 → NRS.04
    // Response must survive the membrane or the patient sees nothing.
    setIsTyping(true);
    try {
      const response = await orchestrator.nurseRespond(
        chatHistoryRef.current,
        systemPrompt,
        text,
      );

      setIsTyping(false);

      if (response) {
        // Check for modal/protocol markers
        const marker = findMarker(response);
        if (marker) {
          handleMarkerInResponse(marker);
        }

        // Strip markers from display text
        const displayText = stripMarkers(response);
        if (displayText) {
          addMsg({ id: nextId(), role: 'nurse', text: displayText });
          chatHistoryRef.current.push({ role: 'model', parts: [{ text: displayText }] });
        }
      }
    } catch (err) {
      setIsTyping(false);
      console.error('[LiveChatEngine] nurseRespond error:', err);
      // Fail closed: show connection error. Never fabricate a nurse response.
      addMsg({ id: nextId(), role: 'system', text: 'Connection issue \u2014 try again' });
    }

    setIsSending(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [inputText, isSending, addMsg, nextId, orchestrator, dataSource, biomarkers, builtProtocol, handleMarkerInResponse]);

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  // Protocol daily price for card
  const dailyPrice = builtProtocol
    ? (builtProtocol.protocol.reduce((sum, p) => {
        const seed = ALL_PRODUCTS_FOR_PRICE.find(s => s.id === p.productId);
        return sum + parseMonthlyPrice(seed?.pricing?.monthlyPrice);
      }, 0) / 30).toFixed(2)
    : '0.00';

  return (
    <div style={{ width: '100%', maxWidth: 480, height: '100vh', margin: '0 auto', display: 'flex', flexDirection: 'column', background: T.bg, fontFamily: F.body, position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0} body{margin:0;background:${T.bg}}
        ::-webkit-scrollbar{width:0;height:0}
        @keyframes td{0%,60%,100%{opacity:.2;transform:translateY(0)}30%{opacity:.8;transform:translateY(-3px)}}
        @keyframes msgIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: T.bgCard, borderBottom: `1px solid ${T.border}`, boxShadow: T.shadowSm, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><HeartPulseIcon /></div>
          <div>
            <div style={{ fontSize: T.fontSize.base, fontWeight: 700, color: T.text }}>Hemo<span style={{ color: T.accent }}>Logix</span></div>
            <div style={{ fontSize: T.fontSize.xs, color: T.textGhost, display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.success }} /> Health Navigator
            </div>
          </div>
        </div>
        <div style={{ fontSize: T.fontSize.xs, fontWeight: 600, color: T.textGhost, display: 'flex', alignItems: 'center', gap: 4 }}><ShieldIcon /> HIPAA</div>
      </div>

      <AnalystBar markers={tickerMarkers} symptoms={symptoms} captainMsg={captainMsg} protocol={protocol} journeyStage="first_visit" />

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ textAlign: 'center', padding: '4px 0 12px' }}>
          <div style={{ display: 'inline-block', fontSize: T.fontSize.xs, fontWeight: 600, color: T.textGhost, background: T.bgSoft, padding: '4px 12px', borderRadius: 20 }}>Free AI Lab Analysis</div>
        </div>

        {messages.map(m => {
          if (m.role === 'system') return (
            <div key={m.id} style={{ textAlign: 'center', padding: '8px 0', animation: 'msgIn 0.3s ease' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: T.fontSize.sm, color: T.success, fontWeight: 600, background: T.successSoft, padding: '6px 14px', borderRadius: 20 }}><FileCheckIcon /> {m.text}</div>
            </div>
          );
          if (m.role === 'user') return (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'flex-end', padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
              <div style={{ maxWidth: '82%', padding: '10px 14px', background: T.accent, color: CONTRAST_WHITE, borderRadius: `${R_LG}px ${R_LG}px ${R}px ${R_LG}px`, fontSize: T.fontSize.sm, lineHeight: 1.55 }}>
                <HighlightedText text={m.text} activeSymptoms={symptoms} />
              </div>
            </div>
          );
          if (m.role === 'nurse') return (
            <div key={m.id} style={{ padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
              <div style={{ maxWidth: '88%', padding: '10px 14px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: `${R_LG}px ${R_LG}px ${R_LG}px ${R}px`, fontSize: T.fontSize.sm, lineHeight: 1.6, color: T.text, boxShadow: T.shadowSm }}>{m.text}</div>
            </div>
          );
          return null;
        })}

        {/* Active modal — inline in chat flow (no GoalsModal — box IS the goal) */}
        {activeModal === 'meds' && (
          <div style={{ padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
            <MedsModal onSubmit={handleMedsSubmit} />
          </div>
        )}
        {activeModal === 'allergies' && (
          <div style={{ padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
            <AllergiesModal onSubmit={handleAllergiesSubmit} />
          </div>
        )}
        {activeModal === 'pregnancy' && (
          <div style={{ padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
            <PregnancyModal onSubmit={handlePregnancySubmit} />
          </div>
        )}

        {showEmailModal && (
          <div style={{ padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
            <EmailSaveModal onSubmit={() => setShowEmailModal(false)} />
          </div>
        )}

        {/* Protocol card — only after [BUILD_PROTOCOL] marker */}
        {showCard && <ProtocolCard dataSource={dataSource} dailyPrice={dailyPrice} />}

        {messages.length > 2 && <SaveProgressBanner messageCount={messages.length} />}

        {isTyping && (
          <div style={{ padding: '3px 0', animation: 'msgIn 0.2s ease' }}>
            <div style={{ display: 'inline-flex', padding: '10px 14px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: `${R_LG}px ${R_LG}px ${R_LG}px ${R}px`, boxShadow: T.shadowSm }}><TypingDots /></div>
          </div>
        )}
        <div style={{ height: 8 }} />
      </div>

      {/* Input — always visible, disabled during modal */}
      <div style={{ padding: '10px 12px 12px', background: T.bgCard, borderTop: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            ref={inputRef}
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={activeModal ? 'Complete the form above first...' : 'Tell me about your health concerns...'}
            disabled={isSending || activeModal !== null}
            style={{
              flex: 1, padding: '10px 14px', fontSize: T.fontSize.sm,
              border: `1px solid ${T.border}`, borderRadius: R_LG,
              outline: 'none', fontFamily: F.body,
              color: T.text, background: T.bg,
              opacity: activeModal ? 0.5 : 1,
            }}
          />
          <button
            onClick={handleSend}
            disabled={isSending || !inputText.trim() || activeModal !== null}
            style={{
              width: 40, height: 40, borderRadius: '50%',
              background: inputText.trim() && !activeModal ? T.accent : T.border,
              border: 'none', cursor: inputText.trim() && !activeModal ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s ease',
              flexShrink: 0,
            }}
          >
            <SendIcon />
          </button>
        </div>
        <div style={{ fontSize: T.fontSize.xs, color: T.textGhost, textAlign: 'center', marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          <ShieldIcon /> Not for diagnostic use &middot; Not a substitute for medical advice
        </div>
      </div>
    </div>
  );
}
